import React from "react";
import type { ApiClient } from "../api/client";
import { authQueryScope } from "../query/authQueryScope";
import { useObjectUrlCache } from "./useObjectUrlCache";

export type PhotoIdentity = { id: string; kind: "persons" | "users"; version: string };
const PHOTO_CONCURRENCY = 4;

/** Authentifizierte Fotos: versioniert, begrenzt parallel, keine doppelten laufenden Abrufe. */
export function usePhotoObjectUrls(api: ApiClient, subjects: PhotoIdentity[]): Record<string, string> {
  const { urls, register } = useObjectUrlCache();
  const { inFlight, completed } = React.useMemo(() => ({
    owner: api,
    inFlight: new Map<string, Promise<Blob>>(),
    completed: new Map<string, string>()
  }), [api]);
  const session = authQueryScope(api, null)[0];
  const storageKeyOf = React.useCallback((subject: PhotoIdentity) =>
    `${session}/${subject.kind}/${subject.id}`, [session]);
  const keyOf = React.useCallback((subject: PhotoIdentity) =>
    `${storageKeyOf(subject)}/${subject.version}`, [storageKeyOf]);
  React.useEffect(() => {
    let alive = true;
    const unique = new Map(subjects.map(subject => [keyOf(subject), subject]));
    const todo = [...unique].filter(([key, subject]) => completed.get(storageKeyOf(subject)) !== key);
    let nextIndex = 0;
    const worker = async () => {
      while (alive && nextIndex < todo.length) {
        const [key, subject] = todo[nextIndex++];
        let request = inFlight.get(key);
        if (!request) {
          request = api.blob(`/api/v1/${subject.kind}/${encodeURIComponent(subject.id)}/photo?v=${encodeURIComponent(subject.version)}`);
          inFlight.set(key, request);
        }
        try {
          const blob = await request;
          const storageKey = storageKeyOf(subject);
          if (!alive || completed.get(storageKey) === key) continue;
          completed.set(storageKey, key);
          register(storageKey, URL.createObjectURL(blob));
        } catch {
          // Optionale Fotos lassen die bestätigten Personen und Initialen sichtbar.
        } finally {
          if (inFlight.get(key) === request) inFlight.delete(key);
        }
      }
    };
    void Promise.all(Array.from({ length: PHOTO_CONCURRENCY }, worker));
    return () => { alive = false; };
  }, [api, subjects, keyOf, storageKeyOf, inFlight, completed, register]);
  return React.useMemo(() => Object.fromEntries(subjects.flatMap(subject => {
    const storageKey = storageKeyOf(subject);
    const url = completed.get(storageKey) === keyOf(subject) ? urls[storageKey] : undefined;
    return url ? [[subject.id, url]] : [];
  })), [subjects, urls, keyOf, storageKeyOf, completed]);
}
